import { TranslateService } from '@ngx-translate/core';

import {
  PropertyImportErrorEntry,
  PropertyImportJobResultResponse,
  PropertyImportJobStatusResponse,
} from '../models/property-import.models';
import { SERVICE_TYPE_TRANSLATION_KEYS } from '../property-list/property-list.constants';

type PropertyImportMessageLike = {
  code?: string;
  messageKey?: string;
  messageParams?: Record<string, unknown>;
  progressMessage?: string;
  lastError?: string;
  sampleErrors?: PropertyImportErrorEntry[];
};

const IMPORT_FIELD_LABEL_KEYS: Record<string, string> = {
  Address: 'PROPERTY_IMPORT.FIELDS.ADDRESS',
  'Caretaker Address': 'PROPERTY_IMPORT.FIELDS.CARETAKER_ADDRESS',
  'Caretaker Email': 'PROPERTY_IMPORT.FIELDS.CARETAKER_EMAIL',
  'Caretaker Phone': 'PROPERTY_IMPORT.FIELDS.CARETAKER_PHONE',
  'Caretaker Mobile': 'PROPERTY_IMPORT.FIELDS.CARETAKER_MOBILE',
  'Property Manager Email': 'PROPERTY_IMPORT.FIELDS.PROPERTY_MANAGER_EMAIL',
  'Property Manager Phone': 'PROPERTY_IMPORT.FIELDS.PROPERTY_MANAGER_PHONE',
  'Property Manager Mobile': 'PROPERTY_IMPORT.FIELDS.PROPERTY_MANAGER_MOBILE',
  'Technical Assets Manager Email': 'PROPERTY_IMPORT.FIELDS.TECHNICAL_ASSETS_MANAGER_EMAIL',
  'Technical Assets Manager Phone': 'PROPERTY_IMPORT.FIELDS.TECHNICAL_ASSETS_MANAGER_PHONE',
  'Technical Assets Manager Mobile': 'PROPERTY_IMPORT.FIELDS.TECHNICAL_ASSETS_MANAGER_MOBILE',
};

const IMPORT_ROLE_LABEL_KEYS: Record<string, string> = {
  'Property Manager': 'PROPERTY_IMPORT.ROLES.PROPERTY_MANAGER',
  'Technical Assets Manager': 'PROPERTY_IMPORT.ROLES.TECHNICAL_ASSETS_MANAGER',
  'Condominium Manager': 'PROPERTY_IMPORT.ROLES.CONDOMINIUM_MANAGER',
  Accountant: 'PROPERTY_IMPORT.ROLES.ACCOUNTANT',
};

const JOB_CODE_FALLBACK_KEYS: Record<string, string> = {
  QUEUED: 'PROPERTY_IMPORT.MESSAGES.QUEUED',
  RESUMED: 'PROPERTY_IMPORT.MESSAGES.RESUMED',
  PREPARING_FILE: 'PROPERTY_IMPORT.MESSAGES.PREPARING_FILE',
  VALIDATING: 'PROPERTY_IMPORT.MESSAGES.VALIDATING',
  PROCESSING_PROGRESS: 'PROPERTY_IMPORT.MESSAGES.PROCESSING_PROGRESS',
  FINALIZING: 'PROPERTY_IMPORT.MESSAGES.FINALIZING',
  COMPLETED: 'PROPERTY_IMPORT.MESSAGES.COMPLETED',
  COMPLETED_WITH_WARNINGS: 'PROPERTY_IMPORT.MESSAGES.COMPLETED_WITH_WARNINGS',
  COMPLETED_WITH_SKIPS: 'PROPERTY_IMPORT.MESSAGES.COMPLETED_WITH_SKIPS',
  COMPLETED_WITH_FAILURES: 'PROPERTY_IMPORT.MESSAGES.COMPLETED_WITH_FAILURES',
  COMPLETED_WITH_FAILURES_AND_SKIPS: 'PROPERTY_IMPORT.MESSAGES.COMPLETED_WITH_FAILURES_AND_SKIPS',
  COMPLETED_UPDATED_ONLY: 'PROPERTY_IMPORT.MESSAGES.COMPLETED_UPDATED_ONLY',
  COMPLETED_CREATED_ONLY: 'PROPERTY_IMPORT.MESSAGES.COMPLETED_CREATED_ONLY',
  COMPLETED_CREATED_AND_UPDATED: 'PROPERTY_IMPORT.MESSAGES.COMPLETED_CREATED_AND_UPDATED',
  COMPLETED_NO_CHANGES: 'PROPERTY_IMPORT.MESSAGES.COMPLETED_NO_CHANGES',
  FAILED_GENERIC: 'PROPERTY_IMPORT.MESSAGES.FAILED_GENERIC',
  COMPANY_NOT_FOUND: 'PROPERTY_IMPORT.ERRORS.COMPANY_NOT_FOUND',
  NO_IMPORTABLE_ROWS: 'PROPERTY_IMPORT.ERRORS.NO_IMPORTABLE_ROWS',
  MISSING_ADDRESS_HEADER: 'PROPERTY_IMPORT.ERRORS.MISSING_ADDRESS_HEADER',
  NO_DATA_ROWS: 'PROPERTY_IMPORT.ERRORS.NO_DATA_ROWS',
  HEADER_ONLY_FILE: 'PROPERTY_IMPORT.ERRORS.HEADER_ONLY_FILE',
  INVALID_FILE: 'PROPERTY_IMPORT.ERRORS.INVALID_FILE',
  VALIDATION_FAILED: 'PROPERTY_IMPORT.ERRORS.VALIDATION_FAILED',
  PROPERTY_LIMIT_REACHED: 'PROPERTY_IMPORT.ERRORS.PROPERTY_LIMIT_REACHED',
  ROW_IMPORT_FAILED: 'PROPERTY_IMPORT.ERRORS.ROW_IMPORT_FAILED',
  DUPLICATE_PROPERTY_ADDRESS: 'PROPERTY_IMPORT.ERRORS.DUPLICATE_PROPERTY_ADDRESS',
  DUPLICATE_PERSISTENCE_CONFLICT: 'PROPERTY_IMPORT.ERRORS.DUPLICATE_PERSISTENCE_CONFLICT',
  INVALID_EMAIL: 'PROPERTY_IMPORT.ERRORS.INVALID_EMAIL',
  INVALID_PHONE: 'PROPERTY_IMPORT.ERRORS.INVALID_PHONE',
  INVALID_ADDRESS: 'PROPERTY_IMPORT.ERRORS.INVALID_ADDRESS',
  MISSING_ADDRESS: 'PROPERTY_IMPORT.ERRORS.MISSING_ADDRESS',
  MISSING_REQUIRED_MANAGER: 'PROPERTY_IMPORT.ERRORS.MISSING_REQUIRED_MANAGER',
  INVALID_CARETAKER_CONTACT: 'PROPERTY_IMPORT.ERRORS.INVALID_CARETAKER_CONTACT',
  AMBIGUOUS_CARETAKER_IDENTITY: 'PROPERTY_IMPORT.ERRORS.AMBIGUOUS_CARETAKER_IDENTITY',
  MULTIPLE_SINGLE_SELECT_ROLE_ASSIGNMENTS: 'PROPERTY_IMPORT.ERRORS.MULTIPLE_SINGLE_SELECT_ROLE_ASSIGNMENTS',
  TECHNICAL_SERVICE_PHONE_OR_MOBILE_REQUIRED: 'PROPERTY_IMPORT.ERRORS.TECHNICAL_SERVICE_PHONE_OR_MOBILE_REQUIRED',
  PROPERTY_MANAGER_EMAIL_REQUIRED: 'PROPERTY_IMPORT.ERRORS.PROPERTY_MANAGER_EMAIL_REQUIRED',
  PROPERTY_MANAGER_PHONE_REQUIRED: 'PROPERTY_IMPORT.ERRORS.PROPERTY_MANAGER_PHONE_REQUIRED',
  PROPERTY_MANAGER_EMAIL_AND_PHONE_REQUIRED: 'PROPERTY_IMPORT.ERRORS.PROPERTY_MANAGER_EMAIL_AND_PHONE_REQUIRED',
  TECHNICAL_ASSETS_MANAGER_EMAIL_REQUIRED: 'PROPERTY_IMPORT.ERRORS.TECHNICAL_ASSETS_MANAGER_EMAIL_REQUIRED',
  TECHNICAL_ASSETS_MANAGER_PHONE_REQUIRED: 'PROPERTY_IMPORT.ERRORS.TECHNICAL_ASSETS_MANAGER_PHONE_REQUIRED',
  TECHNICAL_ASSETS_MANAGER_EMAIL_AND_PHONE_REQUIRED: 'PROPERTY_IMPORT.ERRORS.TECHNICAL_ASSETS_MANAGER_EMAIL_AND_PHONE_REQUIRED',
  SHARED_PROPERTY_MANAGER_FIELD_CONFLICT: 'PROPERTY_IMPORT.ERRORS.SHARED_PROPERTY_MANAGER_FIELD_CONFLICT',
  SHARED_TECHNICAL_ASSETS_MANAGER_FIELD_CONFLICT: 'PROPERTY_IMPORT.ERRORS.SHARED_TECHNICAL_ASSETS_MANAGER_FIELD_CONFLICT',
  SHARED_CARETAKER_FIELD_CONFLICT: 'PROPERTY_IMPORT.ERRORS.SHARED_CARETAKER_FIELD_CONFLICT',
  SHARED_TECHNICAL_SERVICE_FIELD_CONFLICT: 'PROPERTY_IMPORT.ERRORS.SHARED_TECHNICAL_SERVICE_FIELD_CONFLICT',
  NO_FILE_UPLOADED: 'PROPERTY_IMPORT.ERRORS.NO_FILE_UPLOADED',
  UNSUPPORTED_FILE_TYPE: 'PROPERTY_IMPORT.ERRORS.UNSUPPORTED_FILE_TYPE',
  CREATE_JOB_FAILED: 'PROPERTY_IMPORT.ERRORS.CREATE_JOB_FAILED',
  LOAD_JOB_FAILED: 'PROPERTY_IMPORT.ERRORS.LOAD_JOB_FAILED',
  JOB_NOT_FOUND: 'PROPERTY_IMPORT.ERRORS.JOB_NOT_FOUND',
  ACTIVE_JOB_LOOKUP_FAILED: 'PROPERTY_IMPORT.ERRORS.ACTIVE_JOB_LOOKUP_FAILED',
};

function toTechnicalServiceTranslationKey(serviceType: string): string {
  return SERVICE_TYPE_TRANSLATION_KEYS[serviceType]
    || serviceType.replace(/[\s/-]+/g, '_').replace(/[()]/g, '').toUpperCase();
}

function translateFieldLabel(fieldKey: string | undefined, translate: TranslateService): string {
  const normalizedFieldKey = String(fieldKey || '').trim();
  if (!normalizedFieldKey) {
    return '';
  }

  const directKey = IMPORT_FIELD_LABEL_KEYS[normalizedFieldKey];
  if (directKey) {
    return translate.instant(directKey);
  }

  const serviceFieldMatch = normalizedFieldKey.match(/^(.*)\s(Name|Phone|Mobile|Email|Address)$/);
  if (!serviceFieldMatch) {
    return normalizedFieldKey;
  }

  const [, serviceType, part] = serviceFieldMatch;
  const translatedService = translate.instant(`TECHNICAL_SERVICES.${toTechnicalServiceTranslationKey(serviceType)}`);
  const translatedPart = translate.instant(`PROPERTY_IMPORT.FIELD_PARTS.${part.toUpperCase()}`);
  const serviceLabel = translatedService && !translatedService.startsWith('TECHNICAL_SERVICES.')
    ? translatedService
    : serviceType;

  return `${serviceLabel} ${translatedPart}`.trim();
}

function normalizeMessageParams(
  messageParams: Record<string, unknown> | undefined,
  translate: TranslateService
): Record<string, unknown> {
  const params = { ...(messageParams || {}) };
  if (typeof params['fieldKey'] === 'string' && !params['field']) {
    params['field'] = translateFieldLabel(String(params['fieldKey']), translate);
  }
  if (typeof params['role'] === 'string') {
    const roleKey = IMPORT_ROLE_LABEL_KEYS[String(params['role'])];
    if (roleKey) {
      params['role'] = translate.instant(roleKey);
    }
  }
  return params;
}

function translateFromKey(
  messageKey: string | undefined,
  messageParams: Record<string, unknown> | undefined,
  translate: TranslateService
): string | null {
  const normalizedKey = String(messageKey || '').trim();
  if (!normalizedKey) {
    return null;
  }

  const translated = translate.instant(normalizedKey, normalizeMessageParams(messageParams, translate));
  return translated && translated !== normalizedKey ? translated : null;
}

function rawFallbackFromMessage(message: PropertyImportMessageLike): string | null {
  const rawCandidates = [
    message.progressMessage,
    message.lastError,
  ];

  for (const candidate of rawCandidates) {
    const normalized = String(candidate || '').trim();
    if (normalized) {
      return normalized;
    }
  }

  return null;
}

export function translatePropertyImportMessage(
  message: PropertyImportMessageLike | null | undefined,
  translate: TranslateService,
  fallbackKey: string
): string {
  if (message) {
    const keyedMessage = translateFromKey(message.messageKey, message.messageParams, translate);
    if (keyedMessage) {
      return keyedMessage;
    }

    const codeFallbackKey = JOB_CODE_FALLBACK_KEYS[String(message.code || '').trim()];
    const codeFallbackMessage = translateFromKey(codeFallbackKey, message.messageParams, translate);
    if (codeFallbackMessage) {
      return codeFallbackMessage;
    }

    const rawFallback = rawFallbackFromMessage(message);
    if (rawFallback) {
      return rawFallback;
    }
  }

  return translate.instant(fallbackKey);
}

export function translatePropertyImportError(
  errorEntry: PropertyImportErrorEntry,
  translate: TranslateService
): string {
  const translated = translateFromKey(errorEntry.messageKey, errorEntry.messageParams, translate);
  if (translated) {
    return translated;
  }

  const codeFallbackKey = JOB_CODE_FALLBACK_KEYS[String(errorEntry.code || '').trim()];
  const codeFallback = translateFromKey(codeFallbackKey, errorEntry.messageParams, translate);
  if (codeFallback) {
    return codeFallback;
  }

  const rawMessage = String(errorEntry.message || '').trim();
  if (rawMessage) {
    return rawMessage;
  }

  return translate.instant('PROPERTY_IMPORT.ERRORS.ROW_IMPORT_FAILED');
}

export function translatePropertyImportSampleErrors(
  message: PropertyImportMessageLike | null | undefined,
  translate: TranslateService,
  limit = 5
): string[] {
  if (!Array.isArray(message?.sampleErrors)) {
    return [];
  }

  return message.sampleErrors
    .slice(0, Math.max(0, limit))
    .map((entry) => translatePropertyImportError(entry, translate));
}

export function translatePropertyImportBestFailureMessage(
  message: PropertyImportMessageLike | null | undefined,
  translate: TranslateService,
  fallbackKey: string
): string {
  const sampleErrorMessages = translatePropertyImportSampleErrors(message, translate, 1);
  if (sampleErrorMessages.length > 0) {
    return sampleErrorMessages[0];
  }

  return translatePropertyImportMessage(message, translate, fallbackKey);
}

export function translatePropertyImportJobMessage(
  job: PropertyImportJobStatusResponse | PropertyImportJobResultResponse | null | undefined,
  translate: TranslateService,
  fallbackKey: string
): string {
  return translatePropertyImportMessage(job, translate, fallbackKey);
}
